import type {
  ReportingChartSpec,
  ReportingDataset,
  ReportingDatasetRow,
} from 'src/agent/reporting/reportingPresentation.types'

import type {
  ReportingChartPlan,
  ReportingRenderableChartKind,
  ReportingRenderRow,
} from './reportingChart.types'
import {
  categoryValue,
  columnFor,
  currencyDisplayFor,
  encodingFor,
  INVALID_VALUE,
  rawValueFor,
  safeNumericValue,
  supportedKinds,
} from './reportingChartData'
import {
  hasNumericValue,
  pivotRows,
  rowsWithTopNOther,
  sortedRows,
} from './reportingChartPreparation'

const modelForKpi = (
  chartSpec: ReportingChartSpec,
  dataset: ReportingDataset,
  rows: ReportingDatasetRow[],
): ReportingChartPlan | null => {
  const valueReference = encodingFor(chartSpec, 'value')
  if (!valueReference || rows.length !== 1) return null
  if (!columnFor(dataset, valueReference.field)) return null
  const numeric = safeNumericValue(rawValueFor(rows[0], valueReference.field))
  if (numeric === INVALID_VALUE || numeric === null) return null
  const valueCurrency = currencyDisplayFor(
    dataset,
    rows,
    valueReference.field,
    valueReference.format,
  )
  if (valueCurrency === INVALID_VALUE) return null
  return {
    kind: 'kpi',
    data: [{ value: numeric }],
    categoryDataKey: 'value',
    valueDataKeys: ['value'],
    categoryAxis: 'x',
    valueFormat: valueReference.format,
    valueCurrency,
    seriesLabels: {},
  }
}

const modelForCartesian = (
  chartSpec: ReportingChartSpec,
  dataset: ReportingDataset,
  rows: ReportingDatasetRow[],
): ReportingChartPlan | null => {
  const xReference = encodingFor(chartSpec, 'x')
  const yReference = encodingFor(chartSpec, 'y')
  if (!xReference || !yReference) return null
  if (
    !columnFor(dataset, xReference.field) ||
    !columnFor(dataset, yReference.field)
  )
    return null
  const seriesReference = encodingFor(chartSpec, 'series')
  if (seriesReference && !columnFor(dataset, seriesReference.field)) return null

  if (chartSpec.kind === 'line') {
    const pivot = pivotRows(rows, {
      categoryField: xReference.field,
      valueField: yReference.field,
      seriesField: seriesReference?.field,
    })
    if (!pivot || !hasNumericValue(pivot.data, pivot.valueDataKeys)) return null
    const valueCurrency = currencyDisplayFor(
      dataset,
      rows,
      yReference.field,
      yReference.format,
    )
    if (valueCurrency === INVALID_VALUE) return null
    return {
      kind: chartSpec.kind,
      data: pivot.data,
      categoryDataKey: xReference.field,
      valueDataKeys: pivot.valueDataKeys,
      categoryAxis: 'x',
      valueFormat: yReference.format,
      valueCurrency,
      seriesLabels: pivot.seriesLabels,
    }
  }

  const numericXValues = rows.map((row) =>
    safeNumericValue(rawValueFor(row, xReference.field)),
  )
  const numericYValues = rows.map((row) =>
    safeNumericValue(rawValueFor(row, yReference.field)),
  )
  const xIsNumeric = numericXValues.every((value) => value !== INVALID_VALUE)
  const yIsNumeric = numericYValues.every((value) => value !== INVALID_VALUE)

  if (xIsNumeric === yIsNumeric) return null

  const horizontal = xIsNumeric
  const categoryReference = horizontal ? yReference : xReference
  const valueReference = horizontal ? xReference : yReference
  const valueCurrency = currencyDisplayFor(
    dataset,
    rows,
    valueReference.field,
    valueReference.format,
  )
  if (valueCurrency === INVALID_VALUE) return null
  const pivot = pivotRows(rows, {
    categoryField: categoryReference.field,
    valueField: valueReference.field,
    seriesField: seriesReference?.field,
  })
  if (!pivot || !hasNumericValue(pivot.data, pivot.valueDataKeys)) return null
  return {
    kind: chartSpec.kind as
      | 'horizontal_bar'
      | 'grouped_bar'
      | 'stacked_bar'
      | 'diverging_bar',
    data: pivot.data,
    categoryDataKey: categoryReference.field,
    valueDataKeys: pivot.valueDataKeys,
    categoryAxis: horizontal ? 'y' : 'x',
    valueFormat: valueReference.format,
    valueCurrency,
    seriesLabels: pivot.seriesLabels,
  }
}

const modelForPie = (
  chartSpec: ReportingChartSpec,
  dataset: ReportingDataset,
  rows: ReportingDatasetRow[],
): ReportingChartPlan | null => {
  const categoryReference = encodingFor(chartSpec, 'category')
  const valueReference = encodingFor(chartSpec, 'value')
  if (
    !categoryReference ||
    !valueReference ||
    !columnFor(dataset, categoryReference.field) ||
    !columnFor(dataset, valueReference.field) ||
    rows.length === 0 ||
    rows.length > 8
  ) {
    return null
  }
  const valueCurrency = currencyDisplayFor(
    dataset,
    rows,
    valueReference.field,
    valueReference.format,
  )
  if (valueCurrency === INVALID_VALUE) return null
  const data: ReportingRenderRow[] = []
  let total = 0
  for (const row of rows) {
    const category = categoryValue(rawValueFor(row, categoryReference.field))
    const value = safeNumericValue(rawValueFor(row, valueReference.field))
    if (
      category === INVALID_VALUE ||
      category === null ||
      value === INVALID_VALUE ||
      value === null ||
      value < 0
    ) {
      return null
    }
    total += value
    data.push({ [categoryReference.field]: category, value })
  }
  if (total <= 0) return null
  return {
    kind: 'pie',
    data,
    categoryDataKey: categoryReference.field,
    valueDataKeys: ['value'],
    categoryAxis: 'x',
    valueFormat: valueReference.format,
    valueCurrency,
    seriesLabels: {},
  }
}

export const compileReportingChartPlan = (
  chartSpec: ReportingChartSpec,
  dataset: ReportingDataset,
): ReportingChartPlan | null => {
  const kind = chartSpec.kind
  if (
    typeof kind !== 'string' ||
    !supportedKinds.has(kind as ReportingRenderableChartKind) ||
    chartSpec.mark === 'table'
  ) {
    return null
  }

  const sorted = sortedRows(chartSpec, dataset)
  if (!sorted) return null
  const rows = rowsWithTopNOther(chartSpec, sorted)
  if (!rows) return null
  if (kind === 'kpi') return modelForKpi(chartSpec, dataset, rows)
  if (kind === 'pie') return modelForPie(chartSpec, dataset, rows)
  return modelForCartesian(chartSpec, dataset, rows)
}

export const canRenderReportingChart = (
  chartSpec: ReportingChartSpec | null | undefined,
  dataset: ReportingDataset,
): chartSpec is ReportingChartSpec =>
  chartSpec !== null &&
  chartSpec !== undefined &&
  compileReportingChartPlan(chartSpec, dataset) !== null

export type {
  ReportingChartPlan,
  ReportingChartRendererProps,
  ReportingCurrencyDisplay,
  ReportingRenderableChartKind,
  ReportingRenderRow,
  ReportingRenderValue,
} from './reportingChart.types'
